// ========================================
// Distribution View - Short Film Distribution
// ========================================

const DistributionView = {
    films: [],
    students: [],
    studentFilm: null,
    contractTemplates: [],      // Admin-uploaded contract templates
    studentContracts: [],       // Signed contracts for the current student
    isStudentMode: false,
    selectedStudentId: null,
    studentSearchQuery: '',
    activeStudentCategory: 'all',   // a formation type id (Plan de Estudios → Tipos) or 'all'
    activeStudentSubCategory: 'all',
    filmFilter: 'all',           // 'all' | 'submitted'

    render() {
        this.isStudentMode = AuthManager.isStudent();
        
        return `
            <style ${SafeHTML.styleAttr}>
                .switch-toggle {
                    position: relative;
                    display: inline-block;
                    width: 48px;
                    height: 24px;
                }
                .switch-toggle input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .switch-slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: var(--dnr-line-strong);
                    transition: .3s;
                    border-radius: 24px;
                }
                .switch-slider:before {
                    position: absolute;
                    content: "";
                    height: 16px;
                    width: 16px;
                    left: 4px;
                    bottom: 4px;
                    background-color: white;
                    transition: .3s;
                    border-radius: 50%;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                }
                .switch-toggle input:checked + .switch-slider {
                    background-color: #10b981; /* Verde esmeralda */
                }
                .switch-toggle input:focus + .switch-slider {
                    box-shadow: 0 0 1px #10b981;
                }
                .switch-toggle input:checked + .switch-slider:before {
                    transform: translateX(24px);
                }
                /* .dnr-proposal es display:flex;flex-direction:column;gap:1.5rem por defecto —
                   aquí vive en la raíz con varios hijos directos (cabecera, contenedor de
                   contenido), así que ese gap se sumaría a los margin-bottom propios. Anulado
                   desde el principio. */
                .dist-view { gap: 0; }
                .dist-header-title { font-family: var(--dnr-display); font-weight: 400; font-size: 1.9rem; letter-spacing: 0.02em; color: var(--dnr-ink); margin: 0; display: flex; align-items: center; gap: 0.6rem; line-height: 1.1; }
                .dist-header-desc { color: var(--dnr-ink-mute); font-size: 0.9rem; margin: 0.3rem 0 0 0; }
                /* Phone/tablet: students above the received films; each film's facts in two
                   columns and its buttons wrapping. The grids are inline, hence !important. */
                @media (max-width: 960px) {
                    .dist-layout { grid-template-columns: minmax(0, 1fr) !important; }
                    /* The students card is sticky on desktop; stacked, it would cover the films. */
                    .dist-layout > .card:not(.dcat-card) { padding: 1rem !important; min-width: 0; position: static !important; height: auto !important; }
                    .dist-film-facts { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
                    .dist-layout .card button, .dist-layout .card a.btn { white-space: normal; }
                }
            </style>
            <div class="view-container dnr-proposal dist-view" style="padding-top: 1rem;">
                <!-- Header -->
                <div style="margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem;">
                    <div>
                        <h1 class="dist-header-title">
                            <i class="fas fa-film" style="color: var(--dnr-teal-600);"></i> Distribución de Cortometrajes
                        </h1>
                        <p class="dist-header-desc">
                            ${this.isStudentMode
                                ? 'Completa la ficha técnica y artística de tu cortometraje para su envío a festivales.'
                                : 'Gestiona los cortometrajes presentados por los alumnos y autoriza su participación.'}
                        </p>
                    </div>
                    ${this.isStudentMode ? '' : '<div class="dcat-pills" id="dcat-pills"></div>'}
                </div>

                <div id="distribution-content-container">
                    <div style="text-align: center; padding: 3rem; color: var(--dnr-ink-mute);">
                        <i class="fas fa-spinner fa-spin" style="font-size: 2rem; color: var(--dnr-teal-600); margin-bottom: 1rem;"></i>
                        <p>Cargando información de distribución...</p>
                    </div>
                </div>
            </div>
        `;
    },

    async init() {
        try {
            this.isStudentMode = AuthManager.isStudent();
            if (this.isStudentMode) {
                const currentStudentId = AuthManager.getStudentId();
                this.selectedStudentId = currentStudentId;
                await this.loadStudentData(currentStudentId);
                this.renderStudentPanel();
            } else {
                await this.loadAdminData();
                this.renderAdminPanel();
            }
        } catch (error) {
            console.error('Error al inicializar la sección de distribución:', error);
            const container = document.getElementById('distribution-content-container');
            if (container) {
                SafeHTML.set(container, `
                    <div class="alert alert-danger" style="margin: 2rem 0; padding: 1.5rem; border-radius: 12px;">
                        <h4 style="margin: 0 0 0.5rem 0; font-weight: 700;"><i class="fas fa-exclamation-triangle"></i> Error de carga</h4>
                        <p style="margin: 0;">No se pudo conectar con el servidor para obtener los datos de distribución.</p>
                    </div>
                `);
            }
        }
    },

    async loadStudentData(studentId) {
        const token = AuthManager.getCsrfToken();
        
        // 1. Check if distribution enabled
        const distRes = await fetch(`/api/students/${studentId}/distribution`, {
            headers: { 'X-SICAF-CSRF': token }
        });
        const distData = await distRes.json();
        this.distributionEnabled = distData.distributionEnabled;

        // 2. Load short film if exists
        if (this.distributionEnabled) {
            const filmRes = await fetch(`/api/short-films/student/${studentId}`, {
                headers: { 'X-SICAF-CSRF': token }
            });
            const filmData = await filmRes.json();
            this.studentFilm = filmData.film || null;
        }
    },

    async loadAdminData() {
        const token = AuthManager.getCsrfToken();
        
        // 1. Load all students
        const stdRes = await fetch('/api/students', {
            headers: { 'X-SICAF-CSRF': token }
        });
        const stdData = await stdRes.json();
        this.students = stdData.students || [];

        // 2. Load all submitted short films
        const filmsRes = await fetch('/api/short-films', {
            headers: { 'X-SICAF-CSRF': token }
        });
        const filmsData = await filmsRes.json();
        this.films = filmsData.films || [];

        // 3. Load contract templates
        const tplRes = await fetch('/api/contract-templates', {
            headers: { 'X-SICAF-CSRF': token }
        });
        const tplData = await tplRes.json();
        this.contractTemplates = tplData.templates || [];

        // 4. Fetch signed student contracts in parallel for each film explicitly
        this.allSignedContracts = [];
        if (this.films.length > 0) {
            try {
                const promises = this.films.map(film => 
                    fetch(`/api/student-contracts?studentId=${encodeURIComponent(film.studentId)}`, {
                        headers: { 'X-SICAF-CSRF': token }
                    }).then(res => res.json())
                );
                const results = await Promise.all(promises);
                this.allSignedContracts = results.flatMap(r => r.contracts || []);
            } catch (err) {
                console.error("Error fetching student contracts in parallel:", err);
            }
        }
    },

    renderStudentPanel() {

        const container = document.getElementById('distribution-content-container');
        if (!container) return;

        if (!this.distributionEnabled) {
            SafeHTML.set(container, `
                <div style="background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 16px; padding: 3rem 2rem; text-align: center; box-shadow: 0 4px 15px rgba(0,0,0,0.03); max-width: 600px; margin: 2rem auto;">
                    <div style="width: 72px; height: 72px; border-radius: 50%; background: var(--dnr-teal-100); color: var(--dnr-teal-600); display: flex; align-items: center; justify-content: center; font-size: 2rem; margin: 0 auto 1.5rem auto;">
                        <i class="fas fa-lock"></i>
                    </div>
                    <h3 style="font-size: 1.35rem; font-weight: 700; color: var(--dnr-ink); margin: 0 0 0.75rem 0;">Distribución no habilitada</h3>
                    <p style="color: var(--dnr-ink-mute); font-size: 0.95rem; line-height: 1.6; margin: 0 0 1.5rem 0;">
                        La sección de subida de cortometrajes de fin de curso no está activa en tu ficha de alumno en este momento.
                    </p>
                    <p style="font-size: 0.85rem; color: var(--dnr-ink-faint); margin: 0;">
                        Por favor, contacta con la Coordinación Académica o el Departamento de Distribución para que habiliten el acceso a tu proyecto.
                    </p>
                </div>
            `);
            return;
        }

        this.renderStudentForm();
    },

    // ─── Ficha de película (design B, Oct 2026): one page with a fixed index, like a press kit.
    // The student fills it in (saved as a draft as they go) and sends it; once sent it is read
    // only until Distribución gives it back. Staff read the same dossier and copy from it.
    SECTIONS: [
        { id: 'general', name: 'Ficha técnica', cols: 4, fields: [
            ['title', 'Título original', 2], ['englishTitle', 'Título en inglés', 2], ['director', 'Dirección', 2], ['genre', 'Género'],
            ['runningTime', 'Duración (minutos)'], ['yearOfProduction', 'Año de producción'], ['country', 'País de producción'], ['shootingPlace', 'Lugar de rodaje', 2]] },
        { id: 'synopsis', name: 'Sinopsis', cols: 3, text: true, fields: [['synopsisEs', 'Castellano'], ['synopsisEu', 'Euskera'], ['synopsisEn', 'Inglés']] },
        { id: 'artistic', name: 'Equipo y créditos', cols: 3, fields: [
            ['productionCompany', 'Productora', 1, false], ['script', 'Guion'], ['production', 'Producción'], ['cinematography', 'Dirección de fotografía'],
            ['art', 'Dirección de arte'], ['editing', 'Montaje'], ['sound', 'Diseño de sonido'], ['music', 'Música original'], ['cast', 'Intérpretes', 3, true, true]] },
        { id: 'bio', name: 'Biofilmografía', cols: 3, text: true, fields: [['bioEs', 'Castellano'], ['bioEu', 'Euskera'], ['bioEn', 'Inglés']] },
        { id: 'materials', name: 'Materiales y vídeo', cols: 2, fields: [['videoLink', 'Enlace del corto (Vimeo / YouTube)'], ['promoMaterialsFile', 'Enlace a los materiales de promoción']] }
    ],
    _saveTimer: null,
    _saving: null,
    _savedAt: null,

    // [name, label, span, required, textarea] for every field of a section.
    _fields(section) { return section.fields.map(([name, label, span = 1, required = true, area = !!section.text]) => ({ name, label, span, required, area })); },
    _missing(film, section) { return this._fields(section).filter(f => f.required && !String(film[f.name] || '').trim()); },
    _facts(film) { return [film.genre, film.runningTime && (/min/i.test(film.runningTime) ? film.runningTime : film.runningTime + ' min'), film.country, film.yearOfProduction].filter(Boolean).join(' · '); },
    _fmtDate(value) { return value ? new Date(value).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }) : ''; },

    renderStudentForm() {
        const container = document.getElementById('distribution-content-container');
        if (!container) return;
        const film = this.studentFilm || {};
        if (film.status === 'submitted') { this.renderDossier(film, 'student'); return; }
        const placeholders = { title: 'Título original', englishTitle: 'English title', director: 'Nombre y apellidos', genre: 'Ficción, documental, animación…', runningTime: 'ej. 15',
            yearOfProduction: 'ej. 2026', country: 'País', shootingPlace: 'Localidad', productionCompany: SchoolService.shortName(), cast: 'Intérpretes separados por comas',
            videoLink: 'https://vimeo.com/… (con contraseña si es privado)', promoMaterialsFile: 'Enlace de WeTransfer, Google Drive o Dropbox' };
        const hints = { videoLink: 'El vídeo debe tener la descarga habilitada. Si es privado, incluye la contraseña.', promoMaterialsFile: 'Carpeta con el cartel en alta resolución, fotos promocionales y press kit.' };
        // The sanitizer drops name="title" (it would shadow form.title), so that field is «filmTitle».
        const field = f => {
            const value = Utils.sanitizeHTML(film[f.name] || '');
            const name = f.name === 'title' ? 'filmTitle' : f.name;
            const control = f.area
                ? `<textarea name="${name}" class="dfm-input dfm-area" placeholder="${Utils.sanitizeHTML(placeholders[f.name] || '')}">${value}</textarea>`
                : `<input type="${f.name === 'videoLink' ? 'url' : 'text'}" name="${name}" class="dfm-input" value="${value}" placeholder="${Utils.sanitizeHTML(placeholders[f.name] || '')}">`;
            return `<label class="dfm-field" style="grid-column: span ${f.span};"><span>${f.label}${f.required ? ' *' : ''}</span>${control}${hints[f.name] ? `<small>${hints[f.name]}</small>` : ''}</label>`;
        };
        SafeHTML.set(container, `
            <div class="dfm-page">
                <nav class="dfm-nav" aria-label="Índice de la ficha">
                    <span class="dfm-pill" id="dfm-status"></span>
                    <ol class="dfm-toc" id="dfm-toc"></ol>
                    <button type="button" class="dfm-btn" ${UIEvents.attr("click", function(event,captured){DistributionView.saveAsDraft(event);}, [])}><i class="fas fa-floppy-disk" aria-hidden="true"></i> Guardar borrador</button>
                    <button type="button" class="dfm-btn is-primary" ${UIEvents.attr("click", function(event,captured){DistributionView.submitFinal(event);}, [])}><i class="fas fa-paper-plane" aria-hidden="true"></i> Enviar a Distribución</button>
                    <small class="dfm-muted" id="dfm-saved" aria-live="polite">Se guarda solo como borrador cada vez que sales de un campo.</small>
                </nav>
                <form id="distribution-form" class="dfm-body" novalidate>
                    <input type="hidden" name="studentId" value="${Utils.sanitizeHTML(this.selectedStudentId || '')}">
                    <section class="dfm-hero" id="dfm-hero"></section>
                    ${this.SECTIONS.map((s, i) => `
                        <section class="dfm-card" id="dfm-sec-${s.id}">
                            <div class="dfm-card-head"><h2>${i + 1} · ${s.name}</h2></div>
                            <div class="dfm-grid" style="grid-template-columns: repeat(${s.cols}, minmax(0, 1fr));">${this._fields(s).map(field).join('')}</div>
                        </section>`).join('')}
                    <section class="dfm-card" id="dfm-sec-contracts">
                        <div class="dfm-card-head"><h2>6 · Contratos</h2></div>
                        <div class="dfm-contracts" id="contracts-tab-content"></div>
                    </section>
                    <p class="dfm-muted">* Obligatorio para enviar. Hasta que la envíes, Distribución no la ve como terminada.</p>
                </form>
            </div>`);
        const form = document.getElementById('distribution-form');
        form.addEventListener('submit', e => { e.preventDefault(); this.submitFinal(e); });
        form.addEventListener('input', () => this.updateProgress());
        // Each change (leaving a field) saves the draft a moment later.
        form.addEventListener('change', () => this.queueAutosave());
        this.updateProgress();
        const studentId = this.isStudentMode ? (AuthManager.getCurrentUser()?.studentId || '') : (this.selectedStudentId || film.studentId || '');
        if (studentId) this.loadAndRenderContractsTab(studentId);
    },

    _formFilm() {
        const form = document.getElementById('distribution-form');
        return form ? this.serializeFormData(new FormData(form)) : {};
    },

    // Index marks, «faltan N» and the hero, from what is typed right now.
    updateProgress() {
        const film = this._formFilm();
        const missing = this.SECTIONS.map(s => this._missing(film, s).length);
        const total = missing.reduce((a, b) => a + b, 0);
        const status = document.getElementById('dfm-status');
        if (status) { status.textContent = total ? `Borrador · faltan ${total} ${total === 1 ? 'campo' : 'campos'}` : 'Borrador · lista para enviar'; status.className = `dfm-pill ${total ? 'is-warn' : 'is-ok'}`; }
        const toc = document.getElementById('dfm-toc');
        if (toc) SafeHTML.set(toc, this.SECTIONS.map((s, i) => this._tocItem(s.id, `${i + 1} · ${s.name}`, missing[i] ? `faltan ${missing[i]}` : '✓', !missing[i])).join('')
            + this._tocItem('contracts', '6 · Contratos', '', true));
        const hero = document.getElementById('dfm-hero');
        if (hero) SafeHTML.set(hero, this._heroInner(film, false));
    },

    _tocItem(id, label, mark, ok) {
        return `<li><button type="button" class="dfm-toc-item" ${UIEvents.attr("click", function(event,captured){DistributionView.goToSection(String(captured[0]));}, [id])}>
            <span>${label}</span>${mark ? `<span class="dfm-mark ${ok ? 'is-ok' : 'is-warn'}">${mark}</span>` : ''}</button></li>`;
    },

    goToSection(id) { document.getElementById(`dfm-sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); },

    _heroInner(film, links) {
        const std = this.students.find(s => s.id === film.studentId);
        const by = std ? ` · presentado por ${Utils.sanitizeHTML(`${std.firstName} ${std.lastName}`)}` : '';
        return `<div class="dfm-hero-text">
                ${this._facts(film) ? `<span class="dfm-hero-facts">${Utils.sanitizeHTML(this._facts(film))}</span>` : ''}
                <span class="dfm-hero-title">${Utils.sanitizeHTML(film.title || 'Título del corto')}</span>
                <span class="dfm-hero-sub">${[film.englishTitle, film.director && 'Dir. ' + film.director].filter(Boolean).map(v => Utils.sanitizeHTML(v)).join(' · ')}${links ? by : ''}</span>
            </div>
            ${links ? `<div class="dfm-hero-links">
                ${film.videoLink ? `<a class="dfm-btn is-light" href="${Utils.sanitizeHTML(this._filmLink(film.videoLink))}" target="_blank" rel="noopener"><i class="fas fa-play" aria-hidden="true"></i> Ver vídeo</a>` : ''}
                ${film.promoMaterialsFile ? `<a class="dfm-btn is-ghost" href="${Utils.sanitizeHTML(this._filmLink(film.promoMaterialsFile))}" target="_blank" rel="noopener"><i class="fas fa-download" aria-hidden="true"></i> Materiales de promoción</a>` : ''}
            </div>` : ''}`;
    },

    queueAutosave() {
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => this._saveDraft(true), 700);
    },

    // Saves the form as a draft. Quiet: no toast and no re-render, only «Guardado a las…».
    async _saveDraft(quiet) {
        if (!document.getElementById('distribution-form')) return false;
        if (this._saving) { await this._saving; }
        const data = { ...this._formFilm(), status: 'draft' };
        const saved = document.getElementById('dfm-saved');
        if (saved && quiet) saved.textContent = 'Guardando…';
        this._saving = (async () => {
            try {
                const response = await fetch('/api/short-films', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify(data) });
                const res = await response.json().catch(() => null);
                if (!response.ok || !res?.success) throw new Error(res?.error || 'No se pudo guardar el borrador.');
                this.studentFilm = res.film;
                this._savedAt = new Date();
                const el = document.getElementById('dfm-saved');
                if (el) el.textContent = `Borrador guardado a las ${this._savedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}.`;
                return true;
            } catch (e) {
                const el = document.getElementById('dfm-saved');
                if (el) el.textContent = 'No se pudo guardar el borrador. Vuelve a intentarlo.';
                if (!quiet) Utils.showToast(e.message, 'error');
                return false;
            }
        })();
        const ok = await this._saving;
        this._saving = null;
        return ok;
    },

    // ─── The dossier, read only: staff (copy, PDF, give back) or the student once sent.
    showFilmDetailsModal(filmId) {
        const film = this.films.find(f => f.id === filmId);
        if (!film) { Utils.showToast('No se encontró el cortometraje', 'danger'); return; }
        this.renderDossier(film, 'staff');
    },

    renderDossier(film, mode) {
        const container = document.getElementById('distribution-content-container');
        if (!container) return;
        const staff = mode === 'staff';
        const signed = staff ? (this.allSignedContracts || []).filter(c => c.studentId === film.studentId) : [];
        const canManage = staff && (AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.currentUser?.role === 'coordinator' || AuthManager.hasModule('distribucion', 'manage'));
        const sent = film.status === 'submitted';
        const value = v => String(v || '').trim();
        const copyBtn = (text, label) => `<button type="button" class="dfm-copy" aria-label="Copiar ${Utils.sanitizeHTML(label.toLowerCase())}" ${value(text) ? '' : 'disabled'}
            ${UIEvents.attr("click", function(event,captured){DistributionView.copyText(String(captured[0]),this);}, [value(text)])}>Copiar</button>`;
        const section = (s, i) => {
            const fields = this._fields(s);
            return `<section class="dfm-card" id="dfm-sec-${s.id}">
                <div class="dfm-card-head"><h2>${i + 1} · ${s.name}</h2>${staff ? `<button type="button" class="dfm-copy" ${UIEvents.attr("click", function(event,captured){DistributionView.copyText(String(captured[0]),this);}, [this._sectionText(film, s)])}>Copiar sección</button>` : ''}</div>
                <div class="dfm-read" style="grid-template-columns: repeat(${s.text ? 3 : s.cols}, minmax(0, 1fr));">
                    ${fields.map(f => `<div class="dfm-read-item ${f.area ? 'is-text' : ''}" style="grid-column: span ${s.text ? 1 : f.span};">
                        <span class="dfm-read-head"><span class="dfm-label">${f.label}</span>${staff ? copyBtn(film[f.name], f.label) : ''}</span>
                        ${value(film[f.name]) ? (/^(videoLink|promoMaterialsFile)$/.test(f.name)
                            ? `<a class="dfm-read-value" href="${Utils.sanitizeHTML(this._filmLink(film[f.name]))}" target="_blank" rel="noopener">${Utils.sanitizeHTML(film[f.name])}</a>`
                            : `<p class="dfm-read-value">${Utils.sanitizeHTML(film[f.name])}</p>`) : '<p class="dfm-read-value is-empty">—</p>'}
                    </div>`).join('')}
                </div>
            </section>`;
        };
        const contracts = staff ? `
            <div class="dfm-contract-list">
                ${this.contractTemplates.length ? this.contractTemplates.map(t => {
                    const sc = signed.find(c => c.contractTemplateId === t.id);
                    return sc ? `<a class="dfm-contract is-ok" href="${Utils.sanitizeHTML(sc.filePath)}" target="_blank" rel="noopener">✓ ${Utils.sanitizeHTML(t.name)} · Descargar</a>`
                        : `<span class="dfm-contract is-warn">… ${Utils.sanitizeHTML(t.name)} · pendiente</span>`;
                }).join('') : '<span class="dfm-muted">No hay plantillas de contrato.</span>'}
                ${signed.length ? `<button type="button" class="dfm-copy" ${UIEvents.attr("click", function(event,captured){DistributionView.downloadContractsZip(String(captured[0]));}, [film.studentId])}>Contratos firmados (ZIP)</button>` : ''}
            </div>` : '<div class="dfm-contracts" id="contracts-tab-content"></div>';
        const doneTemplates = staff ? this.contractTemplates.filter(t => signed.some(c => c.contractTemplateId === t.id)).length : 0;
        SafeHTML.set(container, `
            <div class="dfm-page">
                <nav class="dfm-nav" aria-label="Índice de la ficha">
                    ${staff ? `<button type="button" class="dfm-back" ${UIEvents.attr("click", function(event,captured){DistributionView.renderAdminPanel();}, [])}><i class="fas fa-arrow-left" aria-hidden="true"></i> Volver a Distribución</button>` : ''}
                    <span class="dfm-pill ${sent ? 'is-ok' : 'is-warn'}">${sent ? `Enviada${film.updatedAt ? ' el ' + this._fmtDate(film.updatedAt) : ''}` : 'Borrador del alumno'}</span>
                    <ol class="dfm-toc">
                        ${this.SECTIONS.map((s, i) => { const n = this._missing(film, s).length; return this._tocItem(s.id, `${i + 1} · ${s.name}`, n ? `faltan ${n}` : '✓', !n); }).join('')}
                        ${this._tocItem('contracts', '6 · Contratos', staff && this.contractTemplates.length ? `${doneTemplates}/${this.contractTemplates.length}` : '', doneTemplates === this.contractTemplates.length)}
                    </ol>
                    ${staff ? `
                        <button type="button" class="dfm-btn is-primary" ${UIEvents.attr("click", function(event,captured){DistributionView.copyText(String(captured[0]),this);}, [this._filmText(film)])}><i class="fas fa-copy" aria-hidden="true"></i> Copiar toda la ficha</button>
                        <button type="button" class="dfm-btn" ${UIEvents.attr("click", function(event,captured){DistributionView.downloadFilmPdf(String(captured[0]));}, [film.id])}><i class="fas fa-file-pdf" aria-hidden="true"></i> Descargar ficha (PDF)</button>
                        ${canManage && sent ? `<button type="button" class="dfm-btn" ${UIEvents.attr("click", function(event,captured){DistributionView.returnFilm(String(captured[0]));}, [film.id])}><i class="fas fa-rotate-left" aria-hidden="true"></i> Devolver al alumno para corregir</button>
                            <small class="dfm-muted">«Devolver» la deja otra vez en borrador y avisa al alumno.</small>` : ''}
                    ` : `<p class="dfm-muted">Tu ficha está enviada. Si necesitas cambiar algo, pide a Distribución que te la devuelva para corregirla.</p>`}
                </nav>
                <div class="dfm-body">
                    <section class="dfm-hero">${this._heroInner(film, true)}</section>
                    ${this.SECTIONS.map(section).join('')}
                    <section class="dfm-card" id="dfm-sec-contracts"><div class="dfm-card-head"><h2>6 · Contratos</h2></div>${contracts}</section>
                </div>
            </div>`);
        container.scrollIntoView({ block: 'start' });
        if (!staff) {
            const studentId = AuthManager.getCurrentUser()?.studentId || film.studentId || '';
            if (studentId) this.loadAndRenderContractsTab(studentId);
        }
    },

    _sectionText(film, section) {
        return `${section.name.toUpperCase()}\n` + this._fields(section).map(f => `${f.label}: ${String(film[f.name] || '').trim() || '—'}`).join(section.text ? '\n\n' : '\n');
    },
    _filmText(film) { return [`${film.title || ''}${film.englishTitle ? ' (' + film.englishTitle + ')' : ''}`, ...this.SECTIONS.map(s => this._sectionText(film, s))].join('\n\n'); },

    async copyText(text, btn) {
        try {
            if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
            else {
                const area = document.createElement('textarea');
                area.value = text; area.setAttribute('readonly', ''); area.style.position = 'fixed'; area.style.opacity = '0';
                document.body.appendChild(area); area.select(); document.execCommand('copy'); area.remove();
            }
            if (btn) { const old = btn.textContent; btn.textContent = '✓ Copiado'; setTimeout(() => { if (btn.isConnected) btn.textContent = old; }, 1500); }
        } catch {
            Utils.showToast('No se pudo copiar. Selecciona el texto y cópialo a mano.', 'error');
        }
    },

    // The whole sheet as a PDF (jsPDF, loaded on first use), section by section.
    downloadFilmPdf(filmId) {
        const film = this.films.find(f => f.id === filmId);
        if (!film) return;
        if (!Utils.whenPdfReady(() => this.downloadFilmPdf(filmId))) return;
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF({ unit: 'mm', format: 'a4' });
        const W = 210, M = 18, width = W - M * 2;
        let y = M;
        const need = h => { if (y + h > 297 - M) { doc.addPage(); y = M; } };
        doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
        doc.splitTextToSize(film.title || 'Sin título', width).forEach(line => { need(9); doc.text(line, M, y + 7); y += 9; });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(85, 102, 95);
        const sub = [film.englishTitle, film.director && 'Dir. ' + film.director, this._facts(film)].filter(Boolean).join(' · ');
        if (sub) doc.splitTextToSize(sub, width).forEach(line => { need(6); doc.text(line, M, y + 4); y += 6; });
        y += 4;
        this.SECTIONS.forEach((s, i) => {
            need(14); doc.setTextColor(14, 107, 98); doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
            doc.text(`${i + 1} · ${s.name}`, M, y + 6); y += 9;
            doc.setDrawColor(219, 227, 223); doc.line(M, y, W - M, y); y += 4;
            this._fields(s).forEach(f => {
                const text = String(film[f.name] || '').trim() || '—';
                const lines = doc.splitTextToSize(text, width);
                need(6 + Math.min(lines.length, 3) * 5);
                doc.setTextColor(85, 102, 95); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
                doc.text(f.label.toUpperCase(), M, y + 3); y += 5;
                doc.setTextColor(15, 27, 25); doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5);
                lines.forEach(line => { need(5); doc.text(line, M, y + 4); y += 5; });
                y += 2.5;
            });
            y += 3;
        });
        const name = (film.title || 'corto').replace(/[\\/:*?"<>|]+/g, '').trim().slice(0, 80) || 'corto';
        doc.save(`Ficha - ${name}.pdf`);
    },

    async returnFilm(filmId) {
        const film = this.films.find(f => f.id === filmId);
        if (!film) return;
        const result = await SafeHTML.fire({ title: '¿Devolver la ficha al alumno?', text: 'Volverá a estar en borrador para que la corrija y la envíe otra vez. Le llegará un aviso.',
            input: 'textarea', inputLabel: 'Qué tiene que corregir (opcional, lo verá el alumno)', inputPlaceholder: 'Ej.: falta la sinopsis en euskera y el enlace no permite descargar',
            showCancelButton: true, confirmButtonText: 'Devolver', cancelButtonText: 'Cancelar', confirmButtonColor: '#0E6B62', reverseButtons: true });
        if (!result.isConfirmed) return;
        try {
            const response = await fetch(`/api/short-films/${encodeURIComponent(filmId)}/return`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                body: JSON.stringify({ message: String(result.value || '').trim().slice(0, 1000) }) });
            const res = await response.json().catch(() => null);
            if (!response.ok || !res?.success) throw new Error(res?.error || 'No se pudo devolver la ficha.');
            Object.assign(film, res.film);
            Utils.showToast('Ficha devuelta al alumno', 'success');
            this.renderDossier(film, 'staff');
        } catch (e) {
            Utils.showToast(e.message, 'error');
        }
    },

    // Staff view (design C, Oct 2026): students and their permission on the left, the films
    // received on the right with each contract's state, the contract templates in one line.
    _filmLink(url) { const u = String(url || ''); return /^https?:\/\//i.test(u) ? u : 'https://' + u; },

    // Where a student's short film stands: sent, draft, not started (with permission) or no permission.
    _studentState(student) {
        const film = this.films.find(f => f.studentId === student.id);
        if (film && film.status === 'submitted') return ['Corto enviado', 'is-ok'];
        if (film) return ['Ficha en borrador', 'is-draft'];
        return student.distributionEnabled ? ['Sin empezar', ''] : ['Sin permiso', 'is-off'];
    },

    _filmRows() {
        const shown = this.filmFilter === 'submitted' ? this.films.filter(f => f.status === 'submitted') : this.films;
        return [...shown].sort((a, b) => (a.status === 'submitted' ? 0 : 1) - (b.status === 'submitted' ? 0 : 1) || String(a.title || '').localeCompare(String(b.title || ''), 'es'));
    },

    renderAdminPanel() {
        const container = document.getElementById('distribution-content-container');
        if (!container) return;
        const sent = this.films.filter(f => f.status === 'submitted').length, drafts = this.films.length - sent;
        const signed = this.allSignedContracts || [];
        const pending = this.contractTemplates.length ? this.films.reduce((n, f) => n + this.contractTemplates.filter(t => !signed.some(c => c.studentId === f.studentId && c.contractTemplateId === t.id)).length, 0) : 0;
        const types = this.studentCategoryTypes();
        const formations = this.getFilteredSubCategories();
        const pills = document.getElementById('dcat-pills');
        if (pills) SafeHTML.set(pills, `
            <span class="dcat-pill is-ok">${sent} ${sent === 1 ? 'enviado' : 'enviados'}</span>
            <span class="dcat-pill">${drafts} ${drafts === 1 ? 'borrador' : 'borradores'}</span>
            ${pending ? `<span class="dcat-pill is-warn">${pending} ${pending === 1 ? 'contrato pendiente' : 'contratos pendientes'}</span>` : ''}`);

        SafeHTML.set(container, `
            <div class="dist-layout dcat-layout">
                <section class="card dcat-card dcat-students">
                    <div class="dcat-students-head">
                        <span class="dcat-label">Alumnos y permisos</span>
                        <input type="search" id="student-search-input" class="dcat-input" placeholder="Buscar alumno" aria-label="Buscar alumno" value="${Utils.sanitizeHTML(this.studentSearchQuery || '')}"
                            ${UIEvents.attr("input", function(event,captured){DistributionView.handleStudentSearch(this.value);}, [])}>
                        <div class="dcat-filters">
                            <select class="dcat-input" aria-label="Tipo de formación" ${UIEvents.attr("change", function(event,captured){DistributionView.switchStudentCategory(this.value);}, [])}>
                                <option value="all">Todos los tipos</option>
                                ${types.map(t => `<option value="${Utils.sanitizeHTML(t.id)}" ${this.activeStudentCategory === t.id ? 'selected' : ''}>${Utils.sanitizeHTML(t.pluralName || t.name)}</option>`).join('')}
                            </select>
                            <select class="dcat-input" aria-label="Formación" ${this.activeStudentCategory === 'all' ? 'disabled' : ''} ${UIEvents.attr("change", function(event,captured){DistributionView.switchStudentSubCategory(this.value);}, [])}>
                                <option value="all">Todas las formaciones</option>
                                ${formations.map(f => `<option value="${Utils.sanitizeHTML(f.id)}" ${this.activeStudentSubCategory === f.id ? 'selected' : ''}>${Utils.sanitizeHTML(f.name)}</option>`).join('')}
                            </select>
                        </div>
                    </div>
                    <div id="admin-student-list-container" class="dcat-student-list"></div>
                </section>

                <div class="dcat-main">
                    <section class="card dcat-card">
                        <div class="dcat-films-head">
                            <span class="dcat-label">Cortos recibidos · ${this.films.length}</span>
                            <select class="dcat-input" aria-label="Qué cortos ver" ${UIEvents.attr("change", function(event,captured){DistributionView.setFilmFilter(this.value);}, [])}>
                                <option value="all" ${this.filmFilter !== 'submitted' ? 'selected' : ''}>Enviados y borradores</option>
                                <option value="submitted" ${this.filmFilter === 'submitted' ? 'selected' : ''}>Solo enviados</option>
                            </select>
                        </div>
                        <div class="dcat-films">${this.renderFilmRows()}</div>
                    </section>
                    <section class="card dcat-card dcat-templates">
                        <span class="dcat-label">Plantillas de contratos</span>
                        <div id="admin-contract-templates-list" class="dcat-template-list">${this.renderAdminContractTemplates()}</div>
                        <label id="add-template-btn" class="dcat-btn is-primary">
                            <i class="fas fa-plus" aria-hidden="true"></i> Añadir plantilla (PDF)
                            <input type="file" id="template-file-input" accept=".pdf,application/pdf" class="dcat-hidden" ${UIEvents.attr("change", function(event,captured){DistributionView.promptAndUploadTemplate(this);}, [])}>
                        </label>
                    </section>
                </div>
            </div>`);
        this.filterAndRenderStudentList();
    },

    setFilmFilter(value) { this.filmFilter = value === 'submitted' ? 'submitted' : 'all'; this.renderAdminPanel(); },

    renderFilmRows() {
        const rows = this._filmRows();
        if (!rows.length) {
            return `<div class="dcat-empty"><i class="fas fa-video-slash" aria-hidden="true"></i><b>${this.films.length ? 'Ningún corto enviado todavía.' : 'Ningún alumno ha empezado su ficha todavía.'}</b><span>Activa el permiso de un alumno para que pueda rellenar la ficha y subir sus materiales.</span></div>`;
        }
        const signed = this.allSignedContracts || [];
        return rows.map(film => {
            const std = this.students.find(s => s.id === film.studentId);
            const studentName = std ? `${std.firstName} ${std.lastName}` : 'Alumno desconocido';
            const mine = signed.filter(c => c.studentId === film.studentId);
            const facts = [film.genre, film.runningTime, film.country, film.yearOfProduction].filter(Boolean).map(v => Utils.sanitizeHTML(v)).join(' · ');
            const contracts = this.contractTemplates.map(t => {
                const ok = mine.some(c => c.contractTemplateId === t.id);
                return `<span class="dcat-chip ${ok ? 'is-ok' : 'is-warn'}" title="${ok ? 'Firmado' : 'Pendiente de firmar'}">${ok ? '✓' : '…'} ${Utils.sanitizeHTML(t.name)}</span>`;
            }).join('');
            return `<div class="dcat-film ${film.status === 'submitted' ? '' : 'is-draft'}">
                <div class="dcat-film-main">
                    <b>${Utils.sanitizeHTML(film.title || 'Sin título')}${film.status === 'submitted' ? '' : ' <span class="dcat-chip">Borrador</span>'}</b>
                    <small>por ${Utils.sanitizeHTML(studentName)}${film.director ? ` · Dir. ${Utils.sanitizeHTML(film.director)}` : ''}</small>
                    ${facts ? `<small>${facts}</small>` : ''}
                </div>
                <div class="dcat-chips">${contracts || '<span class="dcat-muted">Sin plantillas de contrato</span>'}</div>
                <div class="dcat-actions">
                    <button type="button" class="dcat-btn" ${UIEvents.attr("click", function(event,captured){DistributionView.showFilmDetailsModal(String(captured[0]));}, [film.id])}><i class="fas fa-eye" aria-hidden="true"></i> Ficha</button>
                    ${film.videoLink ? `<a class="dcat-btn" href="${Utils.sanitizeHTML(this._filmLink(film.videoLink))}" target="_blank" rel="noopener"><i class="fas fa-play" aria-hidden="true"></i> Vídeo</a>` : ''}
                    ${film.promoMaterialsFile ? `<a class="dcat-btn" href="${Utils.sanitizeHTML(this._filmLink(film.promoMaterialsFile))}" target="_blank" rel="noopener"><i class="fas fa-download" aria-hidden="true"></i> Materiales</a>` : ''}
                    ${mine.length ? `<button type="button" class="dcat-btn" ${UIEvents.attr("click", function(event,captured){DistributionView.downloadContractsZip(String(captured[0]));}, [film.studentId])}><i class="fas fa-file-archive" aria-hidden="true"></i> Contratos (ZIP)</button>` : ''}
                </div>
            </div>`;
        }).join('');
    },

    // Types (Plan de Estudios → Tipos) that have formations, for the student list's filter.
    studentCategoryTypes() {
        const formations = typeof FormationService !== 'undefined' ? FormationService.getFormations() : [];
        return FormationTypeService.getTypes().filter(type => formations.some(f => f.type === type.id));
    },

    getFilteredSubCategories() {
        const formations = typeof FormationService !== 'undefined' ? FormationService.getFormations() : (typeof DataManager !== 'undefined' && DataManager.getFormations ? DataManager.getFormations() : []);
        const cat = this.activeStudentCategory;
        return cat === 'all' ? [] : formations.filter(f => f.type === cat);
    },

    switchStudentCategory(catId) {
        this.activeStudentCategory = catId;
        this.activeStudentSubCategory = 'all'; // Reset sub-category on change
        this.renderAdminPanel();
    },

    switchStudentSubCategory(subCatId) {
        this.activeStudentSubCategory = subCatId;
        this.filterAndRenderStudentList();
    },

    handleStudentSearch(query) {
        this.studentSearchQuery = query.toLowerCase();
        this.filterAndRenderStudentList();
    },

    filterAndRenderStudentList() {
        const listContainer = document.getElementById('admin-student-list-container');
        if (!listContainer) return;

        const query = this.studentSearchQuery || '';
        const cat = this.activeStudentCategory;
        
        // Find submitted short films mapping
        const submittedStudentIds = this.films.map(f => f.studentId);
        
        // Get formations from local cache to map type
        const formations = typeof FormationService !== 'undefined' ? FormationService.getFormations() : (typeof DataManager !== 'undefined' && DataManager.getFormations ? DataManager.getFormations() : []);

        const filtered = this.students.filter(student => {
            // 1. Search Query Filter (Diacritics-insensitive)
            const normalize = str => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            const queryNorm = normalize(query);
            const fullNameNorm = normalize(`${student.firstName} ${student.lastName}`);
            const emailNorm = normalize(student.email || '');
            
            if (queryNorm && !fullNameNorm.includes(queryNorm) && !emailNorm.includes(queryNorm)) {
                return false;
            }

            // 2. Category Filter
            if (cat === 'all') return true;

            const stdFormations = formations.filter(f => (student.formations || []).includes(f.id));

            // Apply sub-category (specific course) filter if active
            if (this.activeStudentSubCategory !== 'all') {
                return (student.formations || []).includes(this.activeStudentSubCategory);
            }

            return cat === 'all' || stdFormations.some(f => f.type === cat);
        });

        if (filtered.length === 0) {
            SafeHTML.set(listContainer, '<p class="dcat-muted dcat-none">No se encontraron alumnos.</p>');
            return;
        }

        SafeHTML.set(listContainer, filtered.map(student => {
            const [state, cls] = this._studentState(student);
            const name = `${student.firstName || ''} ${student.lastName || ''}`.trim();
            return `
                <div class="dcat-student">
                    <span class="dcat-student-main"><b>${Utils.sanitizeHTML(name)}</b><small class="dcat-state ${cls}">${state}</small></span>
                    <label class="switch-toggle" title="Permiso para rellenar la ficha y subir los materiales">
                        <input type="checkbox" aria-label="Permiso de distribución para ${Utils.sanitizeHTML(name)}" ${student.distributionEnabled ? 'checked' : ''}
                            ${UIEvents.attr("change", function(event,captured){DistributionView.toggleDistributionPermission(String(captured[0]),this.checked);}, [student.id])}>
                        <span class="switch-slider"></span>
                    </label>
                </div>`;
        }).join(''));
    },

    async toggleDistributionPermission(studentId, enabled) {
        if (!AuthManager.requireModuleManage("distribucion",["admin","coordinator","rrhh"])) return;
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/students/${studentId}/distribution/toggle`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({ enabled })
            });
            const res = await response.json();
            if (res.success) {
                Utils.showToast(enabled ? 'Distribución activada para el alumno' : 'Distribución desactivada', 'success');
                // Update local model
                const std = this.students.find(s => s.id === studentId);
                if (std) std.distributionEnabled = enabled;
            } else {
                throw new Error(res.error || 'Failed to toggle permission');
            }
        } catch (error) {
            console.error('Error al cambiar permisos de distribución:', error);
            Utils.showToast('Error al modificar permisos: ' + error.message, 'danger');
            this.init(); // Refresh list to revert toggles
        }
    },

    renderAdminContractTemplates() {
        if (this.contractTemplates.length === 0) return '<span class="dcat-muted">No hay plantillas todavía: sube la primera.</span>';
        return this.contractTemplates.map(t => `
            <span class="dcat-template">
                <i class="fas fa-file-pdf" aria-hidden="true"></i>${Utils.sanitizeHTML(t.name)}
                <a class="dcat-btn is-small" href="${Utils.sanitizeHTML(t.filePath)}" target="_blank" rel="noopener">Ver</a>
                <button type="button" class="dcat-btn is-small is-danger" aria-label="Borrar la plantilla ${Utils.sanitizeHTML(t.name)}" ${UIEvents.attr("click", function(event,captured){DistributionView.deleteContractTemplate(String(captured[0]),String(captured[1]));}, [t.id, t.name])}><i class="fas fa-trash-alt" aria-hidden="true"></i></button>
            </span>`).join('');
    },

    showCustomModal({ title, subtitle, placeholder, onConfirm }) {
        // Remove any existing modal
        const existing = document.getElementById('dist-custom-modal');
        if (existing) existing.remove();

        const modal = document.createElement('div');
        modal.id = 'dist-custom-modal';
        modal.style.cssText = `
            position: fixed; inset: 0; z-index: 9999;
            display: flex; align-items: center; justify-content: center;
            background: rgba(15,23,42,0.45); backdrop-filter: blur(4px);
            animation: fadeInModal 0.15s ease;
        `;

        SafeHTML.set(modal, `
            <style ${SafeHTML.styleAttr}>
                @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUpModal { from { transform: translateY(16px) scale(0.97); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
                #dist-modal-box { animation: slideUpModal 0.18s ease; }
                #dist-modal-input:focus { border-color: var(--dnr-teal-600) !important; box-shadow: 0 0 0 3px rgba(20,134,122,0.12); }
            </style>
            <div id="dist-modal-box" class="dnr-proposal" style="
                background: var(--dnr-surface); border-radius: 20px; padding: 2rem 2rem 1.5rem;
                width: 100%; max-width: 440px; margin: 1rem;
                box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
                font-family: 'Inter', system-ui, sans-serif;
                display: block; gap: 0;
            ">
                <!-- Header -->
                <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem;">
                    <div style="flex-shrink:0; width:40px; height:40px; background:var(--dnr-teal-100); border-radius:10px; display:flex; align-items:center; justify-content:center;">
                        <i class="fas fa-file-pdf" style="color:var(--dnr-teal-600); font-size:1.1rem;"></i>
                    </div>
                    <div>
                        <h3 style="margin:0; font-size:1rem; font-weight:700; color:var(--dnr-ink);">${Utils.sanitizeHTML(title)}</h3>
                        ${subtitle ? `<p style="margin:0.2rem 0 0; font-size:0.8rem; color:var(--dnr-ink-mute);">${subtitle}</p>` : ''}
                    </div>
                </div>

                <!-- Input -->
                <label style="display:block; font-size:0.8rem; font-weight:600; color:var(--dnr-ink-mute); margin-bottom:0.4rem;">Nombre de la plantilla</label>
                <input id="dist-modal-input" type="text" placeholder="${placeholder || 'Escribe el nombre...'}"
                    style="width:100%; padding:0.65rem 0.85rem; border:1.5px solid var(--dnr-line); border-radius:10px; font-size:0.9rem; font-family:inherit; outline:none; transition:all 0.15s; color:var(--dnr-ink); box-sizing:border-box; margin-bottom:1.5rem;">

                <!-- Buttons -->
                <div style="display:flex; gap:0.75rem; justify-content:flex-end;">
                    <button id="dist-modal-cancel" style="
                        padding:0.6rem 1.25rem; border:1.5px solid var(--dnr-line); border-radius:10px;
                        background:var(--dnr-surface); color:var(--dnr-ink-mute); font-size:0.85rem; font-weight:600;
                        cursor:pointer; font-family:inherit; transition:all 0.15s;
                    " ${UIEvents.attr("mouseover", function(event,captured){this.style.background='var(--dnr-surface-sunken)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='var(--dnr-surface)';}, [])}>
                        Cancelar
                    </button>
                    <button id="dist-modal-confirm" style="
                        padding:0.6rem 1.5rem; border:none; border-radius:10px;
                        background:var(--dnr-teal-600); color:white; font-size:0.85rem; font-weight:700;
                        cursor:pointer; font-family:inherit; transition:background 0.15s;
                    " ${UIEvents.attr("mouseover", function(event,captured){this.style.background='var(--dnr-teal-700)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='var(--dnr-teal-600)';}, [])}>
                        <i class="fas fa-upload" style="margin-right:5px;"></i> Confirmar
                    </button>
                </div>
            </div>
        `);

        document.body.appendChild(modal);

        const input = modal.querySelector('#dist-modal-input');
        const cancelBtn = modal.querySelector('#dist-modal-cancel');
        const confirmBtn = modal.querySelector('#dist-modal-confirm');

        input.focus();

        const close = () => modal.remove();

        const confirm = () => {
            const val = input.value.trim();
            if (!val) {
                input.style.borderColor = '#ef4444';
                input.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.12)';
                input.placeholder = 'El nombre no puede estar vacío';
                input.focus();
                return;
            }
            close();
            onConfirm(val);
        };

        cancelBtn.addEventListener('click', close);
        confirmBtn.addEventListener('click', confirm);
        modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') confirm();
            if (e.key === 'Escape') close();
        });
    },

    async promptAndUploadTemplate(inputEl) {
        const file = inputEl.files[0];
        if (!file) return;

        this.showCustomModal({
            title: 'Nueva plantilla de contrato',
            subtitle: `Archivo: ${file.name}`,
            placeholder: 'Ej: Contrato de Distribución, Cesión de Derechos...',
            onConfirm: async (name) => {
                const btn = document.getElementById('add-template-btn');
                if (btn) SafeHTML.set(btn, `<i class="fas fa-spinner fa-spin"></i> Subiendo...`);

                const token = AuthManager.getCsrfToken();
                const formData = new FormData();
                formData.append('file', file);
                formData.append('name', name);
                formData.append('order', this.contractTemplates.length.toString());

                try {
                    const res = await fetch('/api/contract-templates', {
                        method: 'POST',
                        headers: { 'X-SICAF-CSRF': token },
                        body: formData
                    });
                    const data = await res.json();
                    if (data.success) {
                        this.contractTemplates.push(data.template);
                        const listEl = document.getElementById('admin-contract-templates-list');
                        if (listEl) SafeHTML.set(listEl, this.renderAdminContractTemplates());
                        Utils.showToast('Plantilla subida correctamente', 'success');
                    } else {
                        throw new Error(data.error);
                    }
                } catch (err) {
                    Utils.showToast('Error al subir plantilla: ' + err.message, 'danger');
                }

                if (btn) SafeHTML.set(btn, `<i class="fas fa-plus"></i> Añadir Plantilla<input type="file" id="template-file-input" accept=".pdf,application/pdf" style="display:none;" ${UIEvents.attr("change", function(event,captured){DistributionView.promptAndUploadTemplate(this);}, [])}>`);
                inputEl.value = '';
            }
        });
        // Reset input immediately so the same file can be re-selected if needed
        inputEl.value = '';
    },


    async deleteContractTemplate(id, name) {
        // Custom confirmation modal instead of native confirm()
        await new Promise((resolve) => {
            const existing = document.getElementById('dist-custom-modal');
            if (existing) existing.remove();

            const modal = document.createElement('div');
            modal.id = 'dist-custom-modal';
            modal.style.cssText = `
                position: fixed; inset: 0; z-index: 9999;
                display: flex; align-items: center; justify-content: center;
                background: rgba(15,23,42,0.45); backdrop-filter: blur(4px);
                animation: fadeInModal 0.15s ease;
            `;
            SafeHTML.set(modal, `
                <style ${SafeHTML.styleAttr}>
                    @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes slideUpModal { from { transform: translateY(16px) scale(0.97); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
                    #dist-modal-box { animation: slideUpModal 0.18s ease; }
                </style>
                <div id="dist-modal-box" class="dnr-proposal" style="
                    background: var(--dnr-surface); border-radius: 20px; padding: 2rem;
                    width: 100%; max-width: 400px; margin: 1rem;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
                    font-family: 'Inter', system-ui, sans-serif;
                    display: block; gap: 0;
                ">
                    <div style="display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.5rem;">
                        <div style="flex-shrink:0; width:42px; height:42px; background:#fef2f2; border-radius:10px; display:flex; align-items:center; justify-content:center;">
                            <i class="fas fa-trash-alt" style="color:#dc2626; font-size:1.1rem;"></i>
                        </div>
                        <div>
                            <h3 style="margin:0 0 0.35rem; font-size:1rem; font-weight:700; color:var(--dnr-ink);">Eliminar plantilla</h3>
                            <p style="margin:0; font-size:0.82rem; color:var(--dnr-ink-mute); line-height:1.5;">
                                ¿Seguro que quieres eliminar <strong style="color:var(--dnr-ink);">"${Utils.sanitizeHTML(name)}"</strong>?<br>
                                <span style="color:var(--dnr-ink-faint);">Se borrarán también todos los contratos firmados vinculados.</span>
                            </p>
                        </div>
                    </div>
                    <div style="display:flex; gap:0.75rem; justify-content:flex-end;">
                        <button id="dist-modal-cancel" style="
                            padding:0.6rem 1.25rem; border:1.5px solid var(--dnr-line); border-radius:10px;
                            background:var(--dnr-surface); color:var(--dnr-ink-mute); font-size:0.85rem; font-weight:600;
                            cursor:pointer; font-family:inherit; transition:all 0.15s;
                        " ${UIEvents.attr("mouseover", function(event,captured){this.style.background='var(--dnr-surface-sunken)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='var(--dnr-surface)';}, [])}>
                            Cancelar
                        </button>
                        <button id="dist-modal-confirm" style="
                            padding:0.6rem 1.25rem; border:none; border-radius:10px;
                            background:#dc2626; color:white; font-size:0.85rem; font-weight:700;
                            cursor:pointer; font-family:inherit; transition:background 0.15s;
                        " ${UIEvents.attr("mouseover", function(event,captured){this.style.background='#b91c1c';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='#dc2626';}, [])}>
                            <i class="fas fa-trash-alt" style="margin-right:5px;"></i> Eliminar
                        </button>
                    </div>
                </div>
            `);
            document.body.appendChild(modal);

            let confirmed = false;
            const close = (ok) => { confirmed = ok; modal.remove(); resolve(ok); };

            modal.querySelector('#dist-modal-cancel').addEventListener('click', () => close(false));
            modal.querySelector('#dist-modal-confirm').addEventListener('click', () => close(true));
            modal.addEventListener('click', (e) => { if (e.target === modal) close(false); });
            document.addEventListener('keydown', function esc(e) {
                if (e.key === 'Escape') { close(false); document.removeEventListener('keydown', esc); }
            });
        }).then(async (confirmed) => {
            if (!confirmed) return;
            const token = AuthManager.getCsrfToken();
            try {
                const res = await fetch(`/api/contract-templates/${id}`, {
                    method: 'DELETE',
                    headers: { 'X-SICAF-CSRF': token }
                });
                const data = await res.json();
                if (data.success) {
                    this.contractTemplates = this.contractTemplates.filter(t => t.id !== id);
                    const listEl = document.getElementById('admin-contract-templates-list');
                    if (listEl) SafeHTML.set(listEl, this.renderAdminContractTemplates());
                    Utils.showToast('Plantilla eliminada', 'success');
                } else {
                    throw new Error(data.error);
                }
            } catch (err) {
                Utils.showToast('Error al eliminar: ' + err.message, 'danger');
            }
        });
    },


    async loadAndRenderContractsTab(studentId) {
        const container = document.getElementById('contracts-tab-content');
        if (!container) return;

        SafeHTML.set(container, `<p style="font-size: 0.85rem; color: var(--dnr-ink-mute); text-align: center; padding: 2rem 0;"><i class="fas fa-spinner fa-spin"></i> Cargando contratos...</p>`);

        const token = AuthManager.getCsrfToken();
        try {
            // Fetch templates (all users can) + student's signed contracts
            const [tplRes, scRes] = await Promise.all([
                fetch('/api/contract-templates', { headers: { 'X-SICAF-CSRF': token } }),
                fetch(`/api/student-contracts?studentId=${encodeURIComponent(studentId)}`, { headers: { 'X-SICAF-CSRF': token } })
            ]);
            const tplData = await tplRes.json();
            const scData = await scRes.json();

            const templates = tplData.templates || [];
            const signed = scData.contracts || [];
            this.studentContracts = signed;

            if (templates.length === 0) {
                SafeHTML.set(container, `<div style="text-align: center; padding: 3rem 1rem; color: var(--dnr-ink-faint);"><i class="fas fa-file-alt" style="font-size: 2rem; margin-bottom: 0.75rem; display: block;"></i><p style="margin: 0; font-size: 0.9rem;">No hay plantillas de contratos todavía.<br>El administrador deberá subirlas primero.</p></div>`);
                return;
            }

            SafeHTML.set(container, `
                <p style="font-size: 0.85rem; color: var(--dnr-ink-mute); margin: 0 0 1.25rem 0; line-height: 1.6;">
                    Descarga la plantilla correspondiente, fírmala y súbela en formato PDF. El archivo quedará guardado directamente.
                </p>
                ${templates.map(t => {
                    const sc = signed.find(s => s.contractTemplateId === t.id);
                    return `
                    <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem 1.25rem; border: 1px solid var(--dnr-line); border-radius: 12px; margin-bottom: 0.75rem; background: var(--dnr-surface-sunken); transition: box-shadow 0.15s;" ${UIEvents.attr("mouseover", function(event,captured){this.style.boxShadow='0 2px 12px rgba(0,0,0,0.07)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.boxShadow='none';}, [])}>
                        <div style="flex-shrink: 0; width: 36px; height: 36px; background: var(--dnr-teal-100); border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                            <i class="fas fa-file-pdf" style="color: var(--dnr-teal-600); font-size: 1rem;"></i>
                        </div>
                        <div style="flex: 1; min-width: 0;">
                            <a href="${Utils.sanitizeHTML(t.filePath)}" target="_blank" style="font-weight: 600; font-size: 0.9rem; color: var(--dnr-teal-600); text-decoration: none; display: flex; align-items: center; gap: 0.4rem;">
                                <i class="fas fa-download" style="font-size: 0.75rem;"></i>
                                ${Utils.sanitizeHTML(t.name)}
                            </a>
                            <div id="sc-filename-${Utils.sanitizeHTML(t.id)}" style="font-size: 0.75rem; color: var(--dnr-ink-mute); margin-top: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                ${sc
                                    ? `<i class="fas fa-check-circle" style="color:#10b981; margin-right:4px;"></i><a href="${Utils.sanitizeHTML(sc.filePath)}" target="_blank" style="color:#10b981; text-decoration:none; font-weight:600;">${Utils.sanitizeHTML(sc.originalName)}</a>`
                                    : '<span style="color:var(--dnr-ink-faint); font-style:italic;">Ningún archivo adjuntado</span>'}
                            </div>
                        </div>
                        <div style="flex-shrink: 0;">
                            <label style="display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.5rem 1rem; background: var(--dnr-teal-600); color: white; border-radius: 8px; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background 0.15s;" ${UIEvents.attr("mouseover", function(event,captured){this.style.background='var(--dnr-teal-700)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='var(--dnr-teal-600)';}, [])}>
                                <i class="fas fa-upload"></i> Subir PDF
                                <input type="file" accept=".pdf,application/pdf" style="display:none;" ${UIEvents.attr("change", function(event,captured){DistributionView.uploadSignedContract(this,String(captured[0]),String(captured[1]));}, [t.id, studentId])}>
                            </label>
                        </div>
                    </div>
                    `;
                }).join('')}
                <p style="font-size: 0.72rem; color: var(--dnr-ink-faint); margin-top: 1rem;"><i class="fas fa-info-circle"></i> Solo se aceptan archivos PDF. Tamaño máximo: 20 MB.</p>
            `);
        } catch (err) {
            SafeHTML.set(container, `<p style="color: #ef4444; font-size: 0.85rem; text-align: center;"><i class="fas fa-exclamation-circle"></i> Error al cargar contratos: ${Utils.sanitizeHTML(err.message)}</p>`);
        }
    },

    async uploadSignedContract(inputEl, templateId, studentId) {
        const file = inputEl.files[0];
        if (!file) return;

        const filenameEl = document.getElementById(`sc-filename-${templateId}`);
        if (filenameEl) SafeHTML.set(filenameEl, `<i class="fas fa-spinner fa-spin" style="color:var(--dnr-ink-mute); margin-right:4px;"></i><span style="color:var(--dnr-ink-mute);">Subiendo...</span>`);

        const token = AuthManager.getCsrfToken();
        const formData = new FormData();
        formData.append('file', file);
        if (studentId) formData.append('studentId', studentId);

        try {
            const res = await fetch(`/api/student-contracts/${templateId}?studentId=${encodeURIComponent(studentId)}`, {
                method: 'POST',
                headers: { 'X-SICAF-CSRF': token },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                if (filenameEl) {
                    SafeHTML.set(filenameEl, `<i class="fas fa-check-circle" style="color:#10b981; margin-right:4px;"></i><a href="${Utils.sanitizeHTML(data.filePath)}" target="_blank" style="color:#10b981; text-decoration:none; font-weight:600;">${Utils.sanitizeHTML(file.name)}</a>`);
                }
                Utils.showToast('Contrato subido correctamente', 'success');
            } else {
                throw new Error(data.error || 'Error al subir');
            }
        } catch (err) {
            if (filenameEl) SafeHTML.set(filenameEl, `<span style="color:#ef4444;"><i class="fas fa-exclamation-circle"></i> Error: ${Utils.sanitizeHTML(err.message)}</span>`);
            Utils.showToast('Error al subir contrato: ' + err.message, 'danger');
        }
        inputEl.value = '';
    },

    async saveAsDraft(e) {
        if (e) e.preventDefault();
        clearTimeout(this._saveTimer);
        if (await this._saveDraft(false)) Utils.showToast('Borrador guardado', 'success');
    },

    async submitFinal(e) {
        if (e) e.preventDefault();
        const form = document.getElementById('distribution-form');
        if (!form) return;
        clearTimeout(this._saveTimer);
        if (this._saving) await this._saving;
        const film = this._formFilm();
        form.querySelectorAll('.dfm-input').forEach(el => el.classList.remove('is-missing'));
        const missing = this.SECTIONS.flatMap(s => this._missing(film, s).map(f => ({ name: f.name, label: f.label, tab: s.id, section: s.name })));
        missing.forEach(f => form.querySelector(`[name="${f.name === 'title' ? 'filmTitle' : f.name}"]`)?.classList.add('is-missing'));
        if (missing.length) { this.showValidationErrorModal(missing); return; }
        const result = await SafeHTML.fire({ title: '¿Enviar la ficha a Distribución?', text: 'Después ya no podrás cambiarla; si hace falta, Distribución te la devolverá para corregirla.', icon: 'question',
            showCancelButton: true, confirmButtonText: 'Enviar', cancelButtonText: 'Seguir revisando', confirmButtonColor: '#0E6B62', reverseButtons: true });
        if (!result.isConfirmed) return;
        await this.postFilmData({ ...film, status: 'submitted' }, 'Ficha enviada a Distribución');
    },

    showValidationErrorModal(missingFields) {
        const existing = document.getElementById('dist-validation-modal');
        if (existing) existing.remove();

        const modal = document.createElement('div');
        modal.id = 'dist-validation-modal';
        modal.style.cssText = `
            position: fixed; inset: 0; z-index: 99999;
            display: flex; align-items: center; justify-content: center;
            background: rgba(15,23,42,0.45); backdrop-filter: blur(4px);
            animation: fadeInModal 0.15s ease;
        `;

        SafeHTML.set(modal, `
            <style ${SafeHTML.styleAttr}>
                @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUpModal { from { transform: translateY(16px) scale(0.97); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
                #dist-val-box { animation: slideUpModal 0.18s ease; }
                .val-err-item:hover { background: #fef2f2 !important; border-color: #fecaca !important; }
            </style>
            <div id="dist-val-box" class="dnr-proposal" style="
                background: var(--dnr-surface); border-radius: 20px; padding: 2rem;
                width: 100%; max-width: 480px; margin: 1rem;
                box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
                font-family: 'Inter', system-ui, sans-serif;
                display: flex; flex-direction: column; gap: 0; max-height: 85vh;
            ">
                <div style="display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.25rem; border-bottom: 1px solid var(--dnr-surface-sunken); padding-bottom: 1rem;">
                    <div style="flex-shrink:0; width:44px; height:44px; background:#fef2f2; border-radius:12px; display:flex; align-items:center; justify-content:center;">
                        <i class="fas fa-exclamation-triangle" style="color:#dc2626; font-size:1.2rem;"></i>
                    </div>
                    <div>
                        <h3 style="margin:0 0 0.25rem; font-size:1.1rem; font-weight:700; color:var(--dnr-ink);">Campos pendientes</h3>
                        <p style="margin:0; font-size:0.82rem; color:var(--dnr-ink-mute); line-height:1.4;">
                            Para enviar la ficha de distribución debes rellenar todos los campos obligatorios. Haz clic en cualquiera para ir a él:
                        </p>
                    </div>
                </div>

                <div style="flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 0.5rem; padding-right: 0.25rem; margin-bottom: 1.5rem;">
                    ${missingFields.map(f => `
                        <div class="val-err-item" ${UIEvents.attr("click", function(event,captured){DistributionView.goToErrorField(String(captured[0]),String(captured[1]));}, [f.tab, f.name])} style="
                            display: flex; align-items: center; justify-content: space-between; gap: 1rem;
                            padding: 0.75rem 1rem; border: 1.5px solid var(--dnr-surface-sunken); border-radius: 10px;
                            background: var(--dnr-surface-sunken); cursor: pointer; transition: all 0.15s;
                        ">
                            <span style="font-size: 0.85rem; font-weight: 600; color: var(--dnr-ink);">${Utils.sanitizeHTML(f.label)}</span>
                            <span style="font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: var(--dnr-teal-600); background: var(--dnr-teal-100); padding: 2px 8px; border-radius: 6px;">
                                ${Utils.sanitizeHTML(f.section || '')}
                            </span>
                        </div>
                    `).join('')}
                </div>

                <div style="display:flex; justify-content:flex-end;">
                    <button ${UIEvents.attr("click", function(event,captured){document.getElementById('dist-validation-modal').remove();}, [])} style="
                        padding:0.65rem 2rem; border:none; border-radius:10px;
                        background:#ef4444; color:white; font-size:0.85rem; font-weight:700;
                        cursor:pointer; font-family:inherit; transition:background 0.15s;
                    " ${UIEvents.attr("mouseover", function(event,captured){this.style.background='#dc2626';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background='#ef4444';}, [])}>
                        Entendido
                    </button>
                </div>
            </div>
        `);

        document.body.appendChild(modal);
        modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
    },

    goToErrorField(tabId, fieldName) {
        document.getElementById('dist-validation-modal')?.remove();
        const input = document.getElementById('distribution-form')?.querySelector(`[name="${fieldName === 'title' ? 'filmTitle' : fieldName}"]`);
        if (input) { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); input.focus({ preventScroll: true }); }
    },

    serializeFormData(formData) {
        return {
            studentId: formData.get('studentId'),
            title: formData.get('filmTitle'),
            director: formData.get('director'),
            englishTitle: formData.get('englishTitle'),
            synopsisEs: formData.get('synopsisEs'),
            synopsisEn: formData.get('synopsisEn'),
            synopsisEu: formData.get('synopsisEu'),
            productionCompany: formData.get('productionCompany'),
            cast: formData.get('cast'),
            script: formData.get('script'),
            production: formData.get('production'),
            cinematography: formData.get('cinematography'),
            art: formData.get('art'),
            editing: formData.get('editing'),
            sound: formData.get('sound'),
            music: formData.get('music'),
            shootingPlace: formData.get('shootingPlace'),
            yearOfProduction: formData.get('yearOfProduction'),
            genre: formData.get('genre'),
            runningTime: formData.get('runningTime'),
            country: formData.get('country'),
            bioEs: formData.get('bioEs'),
            bioEn: formData.get('bioEn'),
            bioEu: formData.get('bioEu'),
            promoMaterialsFile: formData.get('promoMaterialsFile'),
            videoLink: formData.get('videoLink')
        };
    },

    async postFilmData(data, successMessage) {
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch('/api/short-films', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify(data)
            });
            const res = await response.json();
            if (res.success) {
                Utils.showToast(successMessage, 'success');
                this.studentFilm = res.film;
                if (!AuthManager.isStudent()) {
                    await this.loadAdminData();
                    this.renderAdminPanel();
                } else {
                    this.renderStudentForm();
                    document.getElementById('distribution-content-container')?.scrollIntoView({ block: 'start' });
                }
            } else {
                throw new Error(res.error || 'Error al guardar');
            }
        } catch (error) {
            console.error('Error al enviar ficha:', error);
            Utils.showToast('Error al procesar ficha: ' + error.message, 'danger');
        }
    },

    downloadContractsZip(studentId) {
        const token = AuthManager.getCsrfToken();
        if (!token) {
            Utils.showToast('No autorizado. Por favor inicia sesión de nuevo.', 'danger');
            return;
        }
        PrivateDownloads.download(`/api/student-contracts/zip?studentId=${encodeURIComponent(studentId)}`);
    }
};

window.DistributionView = DistributionView;
